import { AxiosRequestConfig } from "axios";
import * as queryString from "query-string";

import { JSONApiError } from "src/library/utils/errors/apiError";
import useAxios, { IApiResponse } from "src/core/shared/hooks/useAxios";
import {
  RequestConfig,
  UseApiRequest
} from "src/core/shared/api/config.api";
import { Icons } from "src/library/components/Icon/types";
import cacheMinutesToMilliseconds from "src/library/utils/time-helpers/cache-minutes-to-milliseconds";

import { EndpointConfig } from "../../../core/shared/api/config.api";

type ProductTagsEndpoint =
  | "CREATE_PRODUCT_TAG"
  | "BULK_CREATE_PRODUCT_TAG"
  | "BULK_UPDATE_PRODUCT_TAG"
  | "DELETE_PRODUCT_TAG"
  | "BULK_DELETE_PRODUCT_TAG"
  | "LIST_PRODUCT_TAGS";

export type CreateProductTag = Pick<IProductTag, "productId" | "tagId"> & Partial<Omit<IProductTag, "id" | "productId" | "tagId">>;
export type BulkCreateTag = Array<Omit<ITag, "id">>;
export type BulkUpdateTag = Array<Partial<ITag> & { id: number }>;
export type DeleteProductTag = { id: number };
export type BulkDeleteProductTag = number[];
export type ListTags = { filters?: Partial<ITag>; filterBySettings?: boolean };

type ProductTagEndpointConfig = {
  CREATE_PRODUCT_TAG: EndpointConfig<
    CreateProductTag,
    IProductTag
  >;
  BULK_CREATE_PRODUCT_TAG: EndpointConfig<
    BulkCreateTag,
    TagBulkCreateResponse
  >;

  BULK_UPDATE_PRODUCT_TAG: EndpointConfig<
    BulkUpdateTag,
    TagBulkUpdateResponse
  >;
  BULK_DELETE_PRODUCT_TAG: EndpointConfig<
    BulkDeleteProductTag,
    TagBulkDeleteResponse
  >;

  DELETE_PRODUCT_TAG: EndpointConfig<
    DeleteProductTag,
    IProductTag
  >;

  LIST_PRODUCT_TAGS: EndpointConfig<
    ListTags,
    ITag[]
  >;
};

const requests: RequestConfig<ProductTagEndpointConfig> = {
  CREATE_PRODUCT_TAG: data => ({
    url: `${process.env.REACT_APP_API_URL}/product-tag`,
    method: "POST",
    data
  }),
  BULK_CREATE_PRODUCT_TAG: data => ({
    url: `${process.env.REACT_APP_API_URL}/product-tag/tags/bulk/create`,
    method: "POST",
    data
  }),
  BULK_UPDATE_PRODUCT_TAG: data => ({
    url: `${process.env.REACT_APP_API_URL}/product-tag/tags/bulk/update`,
    method: "PATCH",
    data
  }),

  BULK_DELETE_PRODUCT_TAG: data => ({
    url: `${process.env.REACT_APP_API_URL}/product-tag/tags/bulk/delete`,
    method: "DELETE",
    data
  }),
  DELETE_PRODUCT_TAG: data => ({
    url: `${process.env.REACT_APP_API_URL}/product-tag/tags/${data.id}`,
    method: "DELETE"
  }),
  LIST_PRODUCT_TAGS: filters => ({
    url: `${process.env.REACT_APP_API_URL}/product-tag/tags/all`,
    method: "GET",
    params: filters
  })
};

export const useProductTagsApi: UseApiRequest<ProductTagEndpointConfig> = (name, axiosOverrides) => useAxios(requests[ name ], name, axiosOverrides);

export interface TagBulkUpdateResponse {
  data: Array<Partial<ITag & { id: number }>>;
  errors: Array<JSONApiError & { data: Partial<ITag & { id: number }> }>;
}

export interface TagBulkCreateResponse {
  data: Array<Partial<ITag>>;
  errors: Array<JSONApiError & { data: Partial<ITag> }>;
}

export interface TagBulkDeleteResponse {
  errors: Array<JSONApiError & { data: { id: number } }>;
}

export interface IProductTag {
  id: number;
  productId: number;
  tagId: number;
}

export interface ITag {
  id: number;
  label: string;
  type: ProductTagType;
  info: string;
  icon?: AvailableProductTagIcons | null;
  iconCode?: string | null;
}

export type ProductTagType = "Allergen" | "Dietary & lifestyle" | "Advisory";
export type AvailableProductTagIcons =
  Icons.EGG |
  Icons.FISH |
  Icons.SHELLFISH |
  Icons.NUTS |
  Icons.ADVISORY |
  Icons.DAIRY |
  Icons.WHEAT |
  Icons.VEGAN |
  Icons.OVEN |
  Icons.FROZEN |
  Icons.PAN |
  Icons.MUSTARD |
  Icons.HALAL |
  Icons.SESAME |
  Icons.LUPIN |
  Icons.PEANUTS|
  Icons.SULPHITES|
  Icons.PORK|
  Icons.MOLLUSCS|
  Icons.SUGAR|
  Icons.CORN|
  Icons.TRANSFATS |
  Icons.SOY |
  Icons.CELERY |
  Icons.ALCOHOL |
  Icons.MEAT |
  Icons.SHELLFISH1 |
  Icons.VEGAN1;
